import type { MouseEvent } from 'react';
import { useMemo } from 'react';
import { Box, Button, Chip, Divider, Menu, MenuItem, Stack, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import type { Dayjs } from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import RouletteContentSubFilter from './RouletteContentSubFilter';
import type { ConversionStep } from './RouletteConversionChart';
import RouletteChartGraph from './RouletteChartGraph';
import { getConversionDataForContent } from './rouletteContentSteps';
import RouletteKpiCard, { type RouletteKpiCardData } from './RouletteKpiCard';
import RouletteTubeFishingTable, {
  type RouletteTubeFishingRow,
} from './table/RouletteTubeFishingTable';
import RouletteConversionStepsTable, {
  type RouletteConversionStepsRow,
} from './table/RouletteConversionStepsTable';
import RouletteRandomAdTable, { type RouletteRandomAdRow } from './table/RouletteRandomAdTable';
import RouletteVideoAdTable, { type RouletteVideoAdRow } from './table/RouletteVideoAdTable';
import RouletteHomeVisitTable, { type RouletteHomeVisitRow } from './table/RouletteHomeVisitTable';
import { PAGE_CARD_SX } from '../../../../theme/pageCardSx';

export const ROULETTE_CONTENT_OPTIONS = [
  '튜브 낚시',
  '뜰채 낚시',
  '통발 수리',
  '통발 건지기',
  '낚시운세',
  '랜덤 광고 보기',
  '광고보고 티켓 얻기',
  '영상 광고 보기',
  '홈 방문하기',
  '첫 방문 보상',
] as const;

export const TUBE_FILTER_OPTIONS = [
  '선택 안함',
  '도넛 튜브',
  '수박 튜브',
  '오렌지 튜브',
  '키위 튜브',
  '파인애플 튜브',
] as const;

export const TIMING_FILTER_OPTIONS = ['보너스 티켓 미당첨', '보너스 티켓 당첨'] as const;

type Props = {
  kpiData: RouletteKpiCardData;
  selectedContent: string;
  onContentChange: (value: string) => void;
  contentAnchorEl: HTMLElement | null;
  onContentAnchor: (el: HTMLElement | null) => void;
  showPeriodCompare: boolean;
  onTogglePeriodCompare: () => void;
  periodCompareStartDate: Dayjs | null;
  periodCompareEndDate: Dayjs | null;
  onPeriodCompareStartChange: (date: Dayjs | null) => void;
  onPeriodCompareEndChange: (date: Dayjs | null) => void;
  mainStartDate: Dayjs | null;
  mainEndDate: Dayjs | null;
  tubeFilter: string;
  onTubeFilterChange: (value: string) => void;
  timingFilter: string;
  onTimingFilterChange: (value: string) => void;
  homevisitConversionData?: ConversionStep[];
  homevisitRows?: RouletteHomeVisitRow[];
  videoAdConversionData?: ConversionStep[];
  videoAdRows?: RouletteVideoAdRow[];
  randomAdConversionData?: ConversionStep[];
  randomAdRows?: RouletteRandomAdRow[];
  adticketConversionData?: ConversionStep[];
  adticketRows?: RouletteConversionStepsRow[];
  tubeConversionData?: ConversionStep[];
  tubeRows?: RouletteTubeFishingRow[];
  tubeChoicePercentages?: Record<string, number> | null;
  moreGameTicketPercentages?: Record<string, number> | null;
  scoopConversionData?: ConversionStep[];
  scoopRows?: RouletteConversionStepsRow[];
  repairConversionData?: ConversionStep[];
  repairRows?: RouletteConversionStepsRow[];
  pullupConversionData?: ConversionStep[];
  pullupRows?: RouletteConversionStepsRow[];
  fortuneConversionData?: ConversionStep[];
  fortuneRows?: RouletteConversionStepsRow[];
  firstVisitConversionData?: ConversionStep[];
  firstVisitRows?: RouletteConversionStepsRow[];
  compareConversionData?: ConversionStep[] | null;
  tubeCompareChoicePercentages?: Record<string, number> | null;
  moreGameTicketComparePercentages?: Record<string, number> | null;
  homevisitCompareRows?: RouletteHomeVisitRow[];
  videoAdCompareRows?: RouletteVideoAdRow[];
  randomAdCompareRows?: RouletteRandomAdRow[];
  adticketCompareRows?: RouletteConversionStepsRow[];
  tubeCompareRows?: RouletteTubeFishingRow[];
  scoopCompareRows?: RouletteConversionStepsRow[];
  repairCompareRows?: RouletteConversionStepsRow[];
  pullupCompareRows?: RouletteConversionStepsRow[];
  fortuneCompareRows?: RouletteConversionStepsRow[];
  firstVisitCompareRows?: RouletteConversionStepsRow[];
  selectedApp?: string | null;
  appOptions?: string[];
  onAppChange?: (app: string) => void;
};

export default function RouletteChartDashboard({
  kpiData,
  selectedContent,
  onContentChange,
  contentAnchorEl,
  onContentAnchor,
  showPeriodCompare,
  onTogglePeriodCompare,
  periodCompareStartDate,
  periodCompareEndDate,
  onPeriodCompareStartChange,
  onPeriodCompareEndChange,
  mainStartDate,
  mainEndDate,
  tubeFilter,
  onTubeFilterChange,
  timingFilter,
  onTimingFilterChange,
  homevisitConversionData,
  homevisitRows,
  videoAdConversionData,
  videoAdRows,
  randomAdConversionData,
  randomAdRows,
  adticketConversionData,
  adticketRows,
  tubeConversionData,
  tubeRows,
  tubeChoicePercentages,
  moreGameTicketPercentages,
  scoopConversionData,
  scoopRows,
  repairConversionData,
  repairRows,
  pullupConversionData,
  pullupRows,
  fortuneConversionData,
  fortuneRows,
  firstVisitConversionData,
  firstVisitRows,
  compareConversionData,
  tubeCompareChoicePercentages,
  moreGameTicketComparePercentages,
  homevisitCompareRows,
  videoAdCompareRows,
  randomAdCompareRows,
  adticketCompareRows,
  tubeCompareRows,
  scoopCompareRows,
  repairCompareRows,
  pullupCompareRows,
  fortuneCompareRows,
  firstVisitCompareRows,
  selectedApp,
  appOptions = [],
  onAppChange,
}: Props) {
  const conversionData = useMemo(
    () =>
      selectedContent === '홈 방문하기' && homevisitConversionData
        ? homevisitConversionData
        : selectedContent === '영상 광고 보기' && videoAdConversionData
          ? videoAdConversionData
          : selectedContent === '랜덤 광고 보기' && randomAdConversionData
            ? randomAdConversionData
            : selectedContent === '광고보고 티켓 얻기' && adticketConversionData
              ? adticketConversionData
              : selectedContent === '튜브 낚시' && tubeConversionData
                ? tubeConversionData
                : selectedContent === '뜰채 낚시' && scoopConversionData
                  ? scoopConversionData
                  : selectedContent === '통발 수리' && repairConversionData
                    ? repairConversionData
                    : selectedContent === '통발 건지기' && pullupConversionData
                      ? pullupConversionData
                      : selectedContent === '낚시운세' && fortuneConversionData
                        ? fortuneConversionData
                        : selectedContent === '첫 방문 보상'
                          ? (firstVisitConversionData ?? [])
                          : getConversionDataForContent(selectedContent),
    [
      selectedContent,
      homevisitConversionData,
      videoAdConversionData,
      randomAdConversionData,
      adticketConversionData,
      tubeConversionData,
      scoopConversionData,
      repairConversionData,
      pullupConversionData,
      fortuneConversionData,
      firstVisitConversionData,
    ],
  );

  const handlePeriodCompareStartChange = (newStart: Dayjs | null) => {
    onPeriodCompareStartChange(newStart);
    if (!newStart) {
      onPeriodCompareEndChange(null);
      return;
    }
    if (mainStartDate && mainEndDate) {
      const days = mainEndDate.diff(mainStartDate, 'day');
      onPeriodCompareEndChange(newStart.add(days, 'day'));
    } else {
      onPeriodCompareEndChange(newStart);
    }
  };

  return (
    <Box
      sx={{
        ...PAGE_CARD_SX,
        width: '100%',
        maxWidth: '100%',
      }}
    >
      {/* 앱 선택 (Chip) - 오퍼월/리텐션 등과 동일한 형태 */}
      {appOptions.length > 0 && onAppChange && (
        <>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              overflowX: 'auto',
              pb: 2,
              mb: 1,
              '&::-webkit-scrollbar': { height: 8 },
              '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
            }}
          >
            {appOptions.map((name) => (
              <Chip
                key={name}
                label={name}
                onClick={() => onAppChange(name)}
                sx={{
                  cursor: 'pointer',
                  fontWeight: selectedApp === name ? 700 : 400,
                  backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                  color: selectedApp === name ? '#fff' : '#374151',
                  border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                  '&:hover': {
                    backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                  },
                  height: 36,
                  fontSize: '0.875rem',
                }}
              />
            ))}
          </Stack>
          <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />
        </>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <Button
          variant="outlined"
          onClick={(e: MouseEvent<HTMLButtonElement>) => onContentAnchor(e.currentTarget)}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            borderColor: '#d0d7e2',
            borderRadius: 2,
            textTransform: 'none',
            color: '#1f2a37',
            minWidth: 220,
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <EmojiObjectsOutlinedIcon fontSize="small" />
            <Stack direction="column" alignItems="flex-start">
              <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 12 }}>
                콘텐츠 선택
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedContent}
              </Typography>
            </Stack>
          </Stack>
        </Button>
        {selectedContent === '튜브 낚시' && (
          <RouletteContentSubFilter
            label="튜브 필터"
            value={tubeFilter}
            options={TUBE_FILTER_OPTIONS}
            onChange={onTubeFilterChange}
          />
        )}
        {selectedContent === '뜰채 낚시' && (
          <RouletteContentSubFilter
            label="필터"
            value={timingFilter}
            options={TIMING_FILTER_OPTIONS}
            onChange={onTimingFilterChange}
          />
        )}
        {/* 기간비교: 점선 박스 + 내부에 버튼/날짜 컴포넌트 */}
        <Box
          sx={{
            minWidth: 220,
            border: '1px dashed #d0d7e2',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            alignSelf: 'center',
          }}
        >
          <Button
            variant="outlined"
            size="small"
            sx={{ alignSelf: 'center', height: 42 }}
            onClick={onTogglePeriodCompare}
          >
            기간비교
          </Button>
          {showPeriodCompare && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    fontWeight={500}
                    fontSize="0.85rem"
                    color="#64748b"
                    sx={{ minWidth: 56, whiteSpace: 'nowrap' }}
                  >
                    시작 날짜
                  </Typography>
                  <DatePicker
                    value={periodCompareStartDate}
                    onChange={handlePeriodCompareStartChange}
                    format="YYYY-MM-DD"
                    slotProps={{
                      textField: {
                        variant: 'standard',
                        InputProps: {
                          disableUnderline: true,
                          sx: {
                            width: { xs: 220, md: 175 },
                            flexShrink: 0,
                            px: 1.5,
                            py: 0,
                            height: 42,
                            borderRadius: 2.5,
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            color: '#1e293b',
                            fontWeight: 500,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#cbd5e1',
                            },
                            '& input': {
                              p: 0,
                              height: '100%',
                              lineHeight: '42px',
                              fontSize: '0.9rem',
                            },
                            '& .MuiInputAdornment-root': { ml: '4px' },
                            '& svg': { color: '#0044D2', fontSize: 20 },
                          },
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    fontWeight={500}
                    fontSize="0.85rem"
                    color="#64748b"
                    sx={{ minWidth: 56, whiteSpace: 'nowrap' }}
                  >
                    종료 날짜
                  </Typography>
                  <DatePicker
                    value={periodCompareEndDate}
                    onChange={onPeriodCompareEndChange}
                    format="YYYY-MM-DD"
                    slotProps={{
                      textField: {
                        variant: 'standard',
                        InputProps: {
                          disableUnderline: true,
                          sx: {
                            width: { xs: 220, md: 175 },
                            flexShrink: 0,
                            px: 1.5,
                            py: 0,
                            height: 42,
                            borderRadius: 2.5,
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            color: '#1e293b',
                            fontWeight: 500,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#cbd5e1',
                            },
                            '& input': {
                              p: 0,
                              height: '100%',
                              lineHeight: '42px',
                              fontSize: '0.9rem',
                            },
                            '& .MuiInputAdornment-root': { ml: '4px' },
                            '& svg': { color: '#0044D2', fontSize: 20 },
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            </LocalizationProvider>
          )}
        </Box>
        <Menu
          anchorEl={contentAnchorEl}
          open={Boolean(contentAnchorEl)}
          onClose={() => onContentAnchor(null)}
        >
          {ROULETTE_CONTENT_OPTIONS.map((opt) => (
            <MenuItem
              key={opt}
              selected={opt === selectedContent}
              onClick={() => {
                onContentAnchor(null);
                onContentChange(opt);
              }}
            >
              {opt}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
      <Box sx={{ mt: 2 }}>
        <RouletteKpiCard data={kpiData} />
      </Box>
      <RouletteChartGraph
        conversionData={conversionData}
        compareConversionData={compareConversionData ?? undefined}
        selectedContent={selectedContent}
        tubeChoicePercentages={tubeChoicePercentages}
        moreGameTicketPercentages={moreGameTicketPercentages}
        tubeCompareChoicePercentages={tubeCompareChoicePercentages ?? undefined}
        moreGameTicketComparePercentages={moreGameTicketComparePercentages ?? undefined}
      />
      {selectedContent === '튜브 낚시' && (
        <RouletteTubeFishingTable rows={tubeRows ?? []} compareRows={tubeCompareRows} />
      )}
      {selectedContent === '뜰채 낚시' && (
        <RouletteConversionStepsTable
          stepCount={9}
          rows={scoopRows ?? []}
          compareRows={scoopCompareRows}
        />
      )}
      {selectedContent === '통발 수리' && (
        <RouletteConversionStepsTable
          stepCount={5}
          rows={repairRows ?? []}
          compareRows={repairCompareRows}
        />
      )}
      {selectedContent === '통발 건지기' && (
        <RouletteConversionStepsTable
          stepCount={5}
          rows={pullupRows ?? []}
          compareRows={pullupCompareRows}
        />
      )}
      {selectedContent === '낚시운세' && (
        <RouletteConversionStepsTable
          stepCount={3}
          rows={fortuneRows ?? []}
          compareRows={fortuneCompareRows}
        />
      )}
      {selectedContent === '랜덤 광고 보기' && (
        <RouletteRandomAdTable rows={randomAdRows ?? []} compareRows={randomAdCompareRows} />
      )}
      {selectedContent === '광고보고 티켓 얻기' && (
        <RouletteConversionStepsTable
          stepCount={6}
          rows={adticketRows ?? []}
          compareRows={adticketCompareRows}
        />
      )}
      {selectedContent === '영상 광고 보기' && (
        <RouletteVideoAdTable rows={videoAdRows ?? []} compareRows={videoAdCompareRows} />
      )}
      {selectedContent === '홈 방문하기' && (
        <RouletteHomeVisitTable rows={homevisitRows ?? []} compareRows={homevisitCompareRows} />
      )}
      {selectedContent === '첫 방문 보상' && (
        <RouletteConversionStepsTable
          stepCount={6}
          rows={firstVisitRows ?? []}
          compareRows={firstVisitCompareRows}
        />
      )}
    </Box>
  );
}
